import {isEqual} from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProtocolVersion} from 'notebook/ELNConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {TABLE_OPS} from 'notebook/tables/augmentNotePartTablesWithValues';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {EvaluationSnapshot} from 'notebook/tables/TableStoreTransforms';
import {isNote, isProtocol, isSimpleTableDoc} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';
import APIUtil from 'util/APIUtil';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {StageRun} from 'workflows/WorkflowConstants';

export function getNoteTableMap(fileId: string, note: Note): FullTableMap {
  const fullTableMap: FullTableMap = {};
  note.noteParts.forEach((notePart) => {
    const type = getNotePartType(notePart);
    if (type == null || (!TABLE_OPS[type] && !(type === ModelTypeConstants.NOTE_LINKED_ASSAY_DATA))) {
      return;
    }

    if (type === ModelTypeConstants.NOTE_LINKED_ASSAY_DATA) {
      if ((notePart.item as NoteLinkedAssayData).versionedData != null) {
        for (const structuredTable of (notePart.item as NoteLinkedAssayData).versionedData.structuredTables) {
          const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
          const table = structuredTableAdapter.toTable(structuredTable);
          fullTableMap[table.name] = table;
        }
      }
    } else {
      // Fetch stage run information for tables in stage entries
      const entryType = APIUtil.apiIdToMaybeType(fileId);
      const isStageEntry =
        entryType === (ModelTypeConstants.STAGE_CONFIGURATION as string) ||
        entryType === (ModelTypeConstants.STAGE_ENTRY as string);
      let stageRuns: Array<StageRun> = [];

      if (isStageEntry) {
        const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
        stageRuns = workflowSelector.getStageRunsForEntry(fileId);
      }
      const table = TABLE_OPS[type].extractTable(notePart, fileId, stageRuns);
      fullTableMap[table.name] = table;
    }
  });
  return fullTableMap;
}

export function getProtocolTableMap(protocol: ProtocolVersion): FullTableMap {
  const fullTableMap: FullTableMap = {};
  protocol.steps.forEach((step) => {
    if (step.table == null) {
      return;
    }
    fullTableMap[step.table.name] = step.table;
  });
  return fullTableMap;
}

/*
 * Validate that the evaluation snapshot and the current note have the same shapes for all the tables
 */
export function isValidEvaluationSnapshot(
  evaluationSnapshot: EvaluationSnapshot,
  item: Note,
  fileId: string,
  fullTableMap: FullTableMap
): boolean {
  for (const [tableName, table] of Object.entries(fullTableMap)) {
    if (!evaluationSnapshot.hasOwnProperty(tableName)) {
      return false;
    }
    const evaluatedTable = evaluationSnapshot[tableName];
    if (evaluatedTable.length !== table.table.length) {
      return false;
    }

    for (let i = 0; i < evaluatedTable.length; i++) {
      if (evaluatedTable[i].length !== table.table[i].length) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Compares evaluationSnapshot dependencies to determine whether a re-evaluation is necessary
 */
export function areEvalSnapshotDepsEqual<ItemType extends Note | ProtocolVersion | SimpleTableDoc>(
  fileId: string,
  oldItem: ItemType | null,
  item: ItemType,
  itemNoteTableMap?: FullTableMap
): boolean {
  if (
    oldItem == null ||
    // For bootstrap case (no evaluation snapshot present), we actually would want to run re-evaluation.
    item.noteRuntimeSnapshot.evaluationSnapshot == null ||
    // We check shallow equality since the evaluationSnapshots should point to the same underlying object if identical
    item.noteRuntimeSnapshot !== oldItem.noteRuntimeSnapshot ||
    item.selectors !== oldItem.selectors
  ) {
    return false;
  }

  // Since we build a new table map for each evaluation snapshot, we compare using deep equality.
  if (isNote(item) && isNote(oldItem)) {
    if (itemNoteTableMap == null) {
      itemNoteTableMap = getNoteTableMap(fileId, item);
    }
    return isEqual(getNoteTableMap(fileId, oldItem), itemNoteTableMap);
  } else if (isProtocol(item) && isProtocol(oldItem)) {
    return isEqual(getProtocolTableMap(item), getProtocolTableMap(oldItem));
  } else if (isSimpleTableDoc(item) && isSimpleTableDoc(oldItem)) {
    return isEqual(item.fullTableMap, oldItem.fullTableMap);
  }
  return false;
}

export function tableProvenanceUuidsForNotePart(notePart: NotePart): Array<string> {
  const type = getNotePartType(notePart);
  if (!type || !TABLE_OPS[type]) {
    return [];
  }
  return TABLE_OPS[type].provenanceUuids(notePart);
}
